<template>
  <div class="pdv-terminal-setup">
    <div class="setup-card">
      <div class="setup-header">
        <img src="media/brand/logo.png" alt="PDV Desktop" class="setup-logo" />
        <h1>Configuração do Terminal</h1>
        <p class="setup-subtitle">Informe o identificador deste terminal para continuar.</p>
      </div>

      <!-- Passo 1: Identificar -->
      <div v-if="passo === 1" class="setup-form">
        <div class="form-group">
          <label for="uuid_terminal">Identificador do Terminal *</label>
          <input
            id="uuid_terminal"
            v-model="form.uuid_terminal"
            type="text"
            class="form-control"
            placeholder="Ex.: caixa-01"
            :class="{ 'is-invalid': erros.uuid_terminal }"
          />
          <div v-if="erros.uuid_terminal" class="invalid-feedback">
            {{ erros.uuid_terminal }}
          </div>
          <small class="form-text text-muted">
            Deve estar cadastrado na sua conta.
          </small>
        </div>

        <div v-if="erroGeral" class="alert alert-danger">{{ erroGeral }}</div>

        <div class="setup-actions">
          <button
            type="button"
            class="btn btn-primary btn-block"
            :disabled="validando"
            @click="validarTerminal"
          >
            <span v-if="validando">
              <span class="spinner-border spinner-border-sm mr-1"></span>
              Validando...
            </span>
            <span v-else>Validar Terminal</span>
          </button>
        </div>
      </div>

      <!-- Passo 2: Confirmar dados e sincronizar -->
      <div v-if="passo === 2" class="setup-form">
        <div class="terminal-info-box">
          <div class="terminal-info-box__row">
            <span class="terminal-info-box__label">Terminal</span>
            <span class="terminal-info-box__value">{{ terminalDados.nome || form.uuid_terminal }}</span>
          </div>
          <div class="terminal-info-box__row">
            <span class="terminal-info-box__label">Série NF-e</span>
            <span class="terminal-info-box__value">{{ terminalDados.serie || '—' }}</span>
          </div>
          <div class="terminal-info-box__row">
            <span class="terminal-info-box__label">Próxima venda</span>
            <span class="terminal-info-box__value">#{{ terminalDados.proxima_venda || 1 }}</span>
          </div>
        </div>

        <div v-if="erroGeral" class="alert alert-danger mt-3">{{ erroGeral }}</div>

        <!-- Progresso de sincronização -->
        <div v-if="sincronizando" class="sync-progress mt-3">
          <div class="progress">
            <div
              class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
              :style="{ width: progresso + '%' }"
            ></div>
          </div>
          <p class="sync-status mt-1">{{ statusSync }}</p>
        </div>

        <div v-if="sucesso" class="alert alert-success mt-3">
          ✓ Terminal configurado! Abrindo o painel...
        </div>

        <div class="setup-actions mt-3">
          <button
            type="button"
            class="btn btn-light mr-2"
            :disabled="carregando"
            @click="passo = 1"
          >
            Voltar
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="carregando"
            @click="confirmarESincronizar"
          >
            <span v-if="carregando">
              <span class="spinner-border spinner-border-sm mr-1"></span>
              Salvando...
            </span>
            <span v-else>Confirmar e Sincronizar</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SyncPdvDesktopService from "../services/SyncPdvDesktopService";
import TerminalConfigService from "../services/TerminalConfigService";
import { SYSTEM_VARIABLE } from "@/modules/Application";

export default {
  name: "PdvTerminalSetup",

  data() {
    return {
      passo: 1,
      form: { uuid_terminal: "" },
      terminalDados: {},
      erros: {},
      erroGeral: null,
      validando: false,
      carregando: false,
      sincronizando: false,
      sucesso: false,
      progresso: 0,
      statusSync: "",
    };
  },

  async mounted() {
    if (!this.$isDesktop) {
      this.$router.push({ name: "pdvDesktopLogin" });
      return;
    }
    // Pré-preenche UUID se já tiver um salvo
    try {
      const info = await this.$electron.terminal.getInfo();
      if (info && info.uuid_terminal) {
        this.form.uuid_terminal = info.uuid_terminal;
      }
    } catch (_) { /* ignore */ }
  },

  methods: {
    async validarTerminal() {
      this.erros = {};
      this.erroGeral = null;

      if (!this.form.uuid_terminal.trim()) {
        this.erros.uuid_terminal = "O identificador do terminal é obrigatório.";
        return;
      }

      this.validando = true;
      try {
        const apiUrl = (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
        if (!apiUrl) throw new Error("URL da API não configurada.");

        const token = await this.$electron.config.get("api_token");
        if (!token) throw new Error("Token não encontrado. Faça login novamente.");

        // Obtém identificação única e permanente deste dispositivo
        const [device_id, device_name] = await Promise.all([
          this.$electron.device.getDeviceId(),
          this.$electron.device.getDeviceName(),
        ]);

        const resp = await fetch(
          `${apiUrl}/api/offline/terminal/${encodeURIComponent(this.form.uuid_terminal.trim())}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ device_id, device_name }),
          }
        );

        if (!resp.ok) {
          let errJson = {};
          try { errJson = await resp.json(); } catch (_) { /* ignore */ }

          if (resp.status === 409) {
            // Terminal já vinculado a outro dispositivo
            throw new Error(
              errJson.message ||
              "Este terminal já está vinculado a outro computador. Para usar neste dispositivo, desvincule o terminal no painel web."
            );
          }
          if (resp.status === 403) {
            throw new Error(errJson.message || "Terminal bloqueado ou inativo. Verifique no painel web.");
          }
          if (resp.status === 404) {
            throw new Error("Terminal não encontrado. Verifique o identificador.");
          }
          throw new Error(errJson.message || `Erro ao validar terminal (HTTP ${resp.status}).`);
        }

        const json = await resp.json();

        // API retornou status false (ex: uuid inválido, terminal não encontrado)
        if (json.status === false || json.status === 0) {
          throw new Error("Terminal não localizado. Verifique o identificador e tente novamente.");
        }

        // Aceita { terminal, configuracao }, { data: ... } ou resposta direta
        const dados    = json.data ?? json;
        const terminal = dados.terminal    ?? dados;
        const config   = dados.configuracao ?? {};
        const nfce     = dados.configuracao_nfce ?? dados.nfce_config ?? null;
        const idConfiguracaoNfce =
          nfce?.id_configuracao ??
          nfce?.id ??
          config.id ??
          dados.id_configuracao ??
          null;


        this.terminalDados = {
          id_terminal_venda: terminal.id_terminal_venda ?? terminal.id ?? null,
          uuid_terminal:     terminal.uuid_terminal ?? terminal.uuid ?? this.form.uuid_terminal.trim(),
          nome:              terminal.nome ?? terminal.name ?? this.form.uuid_terminal.trim(),
          id_configuracao:   idConfiguracaoNfce,
          serie:             nfce?.serie ?? config.serie ?? dados.serie ?? dados.serie_nfe ?? "",
          proxima_venda:     nfce?.proximaVenda ?? nfce?.proxima_venda ?? config.proximaVenda ?? config.proxima_venda ?? dados.proxima_venda ?? dados.proximo_numero ?? 1,
          id_empresa:        terminal.id_empresa ?? dados.id_empresa ?? null,
          configuracao_nfce: nfce,
        };

        this.passo = 2;
      } catch (e) {
        this.erroGeral = e.message || String(e);
      } finally {
        this.validando = false;
      }
    },

    async confirmarESincronizar() {
      this.erroGeral = null;
      this.carregando = true;
      this.sincronizando = false;
      this.progresso = 0;
      this.statusSync = "";

      try {
        const apiUrl = (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
        const token = await this.$electron.config.get("api_token");

        // Persiste todos os dados do terminal
        await this.$electron.terminal.save({
          uuid_terminal:    this.terminalDados.uuid_terminal,
          serie:            this.terminalDados.serie,
          nome:             this.terminalDados.nome,
          id_terminal_venda: this.terminalDados.id_terminal_venda,
          id_configuracao:  this.terminalDados.id_configuracao,
          proxima_venda:    this.terminalDados.proxima_venda,
          id_empresa:       this.terminalDados.id_empresa,
          config:           { api_url: apiUrl },
        });
        await this.$electron.config.set("api_url", apiUrl);
        if (this.terminalDados.configuracao_nfce) {
          await TerminalConfigService.salvarConfiguracaoNfceTerminal(
            this.terminalDados.configuracao_nfce
          );
        } else {
          await TerminalConfigService.limparConfiguracaoNfceTerminal();
        }

        this.sincronizando = true;

        await SyncPdvDesktopService.sincronizarCompleto({
          onProgress: (pct, status) => {
            this.progresso = pct;
            this.statusSync = status;
          },
        });

        this.sincronizando = false;
        this.sucesso = true;

        setTimeout(() => {
          this.$router.push({ name: "pdvDesktopDashboard" });
        }, 1200);
      } catch (e) {
        this.sucesso = false;
        this.sincronizando = false;
        this.erroGeral = `Erro: ${e.message || String(e)}`;
        console.error("[TerminalSetup] confirmarESincronizar:", e);
      } finally {
        this.carregando = false;
      }
    },
  },
};
</script>

<style scoped>
.pdv-terminal-setup {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f7fa;
  padding: 2rem;
}
.setup-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
  padding: 2.5rem;
  width: 100%;
  max-width: 440px;
}
.setup-header {
  text-align: center;
  margin-bottom: 2rem;
}
.setup-logo {
  max-height: 70px;
  margin-bottom: 1rem;
}
.setup-subtitle {
  color: #6c757d;
  font-size: 0.95rem;
}
.setup-actions {
  margin-top: 1.5rem;
  display: flex;
  justify-content: flex-end;
}
.sync-status {
  font-size: 0.85rem;
  color: #6c757d;
}
.terminal-info-box {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  background: #f8f9fa;
}
.terminal-info-box__row {
  display: flex;
  justify-content: space-between;
  padding: 0.3rem 0;
  font-size: 0.9rem;
}
.terminal-info-box__label {
  color: #6c757d;
}
.terminal-info-box__value {
  font-weight: 600;
  color: #212529;
}
</style>
